import React, { useState, useEffect } from 'react';
import { Checkbox } from 'antd';
import styled from 'styled-components';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { Carriage } from 'types';
import { path } from 'ramda';

import { useTheme } from 'common/styling/theme';

type StyledArrowBoxProps = {
  arrowLeft?: boolean;
  withMultipleFloors?: boolean;
  isOuigoSlowOrMargrite?: boolean;
};
type Props = {
  arrowLeft?: boolean;
  trainName: string;
  values: number[];
  withMultipleFloors?: boolean;
  setSelectedCarriages: (state: Carriage[]) => void;
  onTouch: () => void;
  defaultCarriages?: Carriage[];
  equipementCarriages?: number[] | null;
  selectedServiceType?: string;
};
const StyledWrapperArrowBox = styled.div`
  display: flex;
  justify-content: center;
  margin-left: 0.5rem;
  margin-bottom: 0.5rem;
  margin-right: 0.5rem;
`;
const TrainWrapper = styled.div<StyledArrowBoxProps>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.arrowLeft ? 'end' : 'start')};
`;
const Row = styled.div`
  display: flex;
`;
const CarriagesWrapper = styled.div`
  display: flex;
  flex: 1;
`;
const Col = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;
const StyledArrowBox = styled.div<StyledArrowBoxProps>`
  border-radius: 5px;
  position: relative;
  padding: 10px;
  height: ${(props) => (props.withMultipleFloors ? '110px' : '60px')};
  width: 250px;
  margin-right: 2%;

  .checkboxWrapper {
    display: flex;
  }
  .checkboxItem {
    display: block;
    text-align: center;
    font-weight: bold;
    color: #666;
    margin-bottom: ${(props) => (props.isOuigoSlowOrMargrite ? '0' : '16px')};
    margin-top: ${(props) => (props.isOuigoSlowOrMargrite ? '25px' : '0')};
  }
  .ant-checkbox-wrapper + .ant-checkbox-wrapper {
    margin: 0;
  }
`;

const CustomCheckbox = styled(Checkbox)<{ color?: string }>`
  ${({ color }) =>
    color &&
    `
      & {
        .ant-checkbox-checked {
          background-color: ${color};
          border-color: ${color};
        }
        .ant-checkbox-inner {
          background-color: ${color};
          border-color: ${color};
        }
      }
    `}
`;

const CarriageSelection = ({
  arrowLeft,
  trainName,
  values,
  withMultipleFloors,
  setSelectedCarriages,
  onTouch,
  defaultCarriages,
  equipementCarriages,
  selectedServiceType,
}: Props) => {
  const { theme } = useTheme();
  const [carriages, setCarriages] = useState<Carriage[]>([]);
  const [allCarriagesChecked, setAllCarriagesChecked] = useState<boolean>(false);

  useEffect(() => {
    const train: Carriage[] = [];
    values.forEach((carriageNumber: number) => {
      const checkValue = defaultCarriages?.find((elem) => elem.carriageNumber === carriageNumber);
      if (checkValue) {
        train.push(checkValue);
      } else {
        train.push({
          carriageNumber,
          wholeCarriageSelected: false,
          upStage: withMultipleFloors ? false : null,
          downStage: withMultipleFloors ? false : null,
        });
      }
    });
    setCarriages(train);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCarriages]);

  useEffect(() => {
    if (carriages.every((carriage) => carriage.wholeCarriageSelected)) {
      setAllCarriagesChecked(true);
    } else {
      setAllCarriagesChecked(false);
    }
  }, [carriages]);

  useEffect(() => {
    setSelectedCarriages(
      carriages.filter((carriage) => carriage.wholeCarriageSelected || carriage.upStage || carriage.downStage)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carriages]);

  const updateCarriage = (updatedCarriage: Carriage, index: number) => {
    onTouch();
    setCarriages([...carriages.slice(0, index), updatedCarriage, ...carriages.slice(index + 1)]);
  };

  const handleSelectAllCarriage = (e: CheckboxChangeEvent) => {
    carriages.map((carriage, index) =>
      updateCarriage(
        (carriages[index] = {
          carriageNumber: carriages[index].carriageNumber,
          upStage: withMultipleFloors ? e.target.checked : null,
          downStage: withMultipleFloors ? e.target.checked : null,
          wholeCarriageSelected: e.target.checked,
        }),
        index
      )
    );
    setAllCarriagesChecked(e.target.checked);
  };

  return (
    <StyledWrapperArrowBox>
      <TrainWrapper arrowLeft={arrowLeft}>
        <Checkbox
          checked={allCarriagesChecked}
          onChange={(e) => handleSelectAllCarriage(e)}
          style={{ marginBottom: '0.5rem' }}
        >
          {trainName}
        </Checkbox>
        <StyledArrowBox
          arrowLeft={arrowLeft}
          withMultipleFloors={withMultipleFloors}
          isOuigoSlowOrMargrite={['OUIGO_LC', 'OUIGO_LB'].includes(selectedServiceType || '')}
        >
          <Row className="checkboxWrapper">
            <CarriagesWrapper>
              {carriages.map((carriage: Carriage, index) => (
                <Col key={carriage.carriageNumber}>
                  <CustomCheckbox
                    color={
                      equipementCarriages && !equipementCarriages?.includes(carriages[index].carriageNumber)
                        ? path(['colors', 'grey', 'dark'], theme)
                        : undefined
                    }
                    onChange={(e) =>
                      updateCarriage(
                        {
                          carriageNumber: carriages[index].carriageNumber,
                          upStage: withMultipleFloors ? e.target.checked : null,
                          downStage: withMultipleFloors ? e.target.checked : null,
                          wholeCarriageSelected: e.target.checked,
                        },
                        index
                      )
                    }
                    checked={carriage.wholeCarriageSelected}
                  />
                  <div className="checkboxItem">{carriage.carriageNumber}</div>
                  {carriage.upStage !== null && carriage.downStage !== null && (
                    <>
                      <Checkbox
                        onChange={(e) =>
                          carriage.downStage && !carriage.upStage
                            ? updateCarriage(
                                { ...carriage, upStage: e.target.checked, wholeCarriageSelected: true },
                                index
                              )
                            : carriage.downStage && carriage.upStage
                            ? updateCarriage(
                                { ...carriage, upStage: e.target.checked, wholeCarriageSelected: false },
                                index
                              )
                            : updateCarriage({ ...carriage, upStage: e.target.checked }, index)
                        }
                        checked={carriage.upStage}
                      />
                      <Checkbox
                        onChange={(e) =>
                          carriage.upStage && !carriage.downStage
                            ? updateCarriage(
                                { ...carriage, downStage: e.target.checked, wholeCarriageSelected: true },
                                index
                              )
                            : carriage.downStage && carriage.upStage
                            ? updateCarriage(
                                { ...carriage, downStage: e.target.checked, wholeCarriageSelected: false },
                                index
                              )
                            : updateCarriage({ ...carriage, downStage: e.target.checked }, index)
                        }
                        checked={carriage.downStage}
                      />
                    </>
                  )}
                </Col>
              ))}
            </CarriagesWrapper>
          </Row>
        </StyledArrowBox>
      </TrainWrapper>
    </StyledWrapperArrowBox>
  );
};

export default CarriageSelection;
